import { useState } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import OptionDialog from 'components/OptionDialog'
import NumberInput from 'components/Inputs/NumberInput'
import MoneyInput from 'components/Inputs/MoneyInput'
import Select from 'components/Inputs/Select'
import CheckBox from 'components/CheckBox'
import { ReactComponent as ButtonLoading } from 'icons/loading2.svg'
import Logo from 'icons/logo-cutilei-simple.png'
import ReactTooltip from 'react-tooltip'
import { Formik } from 'formik'
import * as FA from 'react-icons/fa'
import { DateTime } from 'luxon'

import {
  Container,
  FormField,
  Row,
  CheckRow,
  Image,
  Label,
  InfoText,
  TooltipLabel,
  MoneyTextMask,
  Button
} from './styles'

export default function BillSchedule ({
  data: schedule,
  services = [],
  professionals = [],
  freeItems = [],
  showFreeItemsValue = false,
  enableEditting = true,
  hasEditPermission = true,
  hasDeletePermission = true,
  onToggleFree: handleToggleFree = () => null,
  onToggleEdit: handleToggleEdit,
  onEdit: handleEdit,
  onDelete: handleDelete,
  onRemoveFromBill: handleRemoveFromBill
}) {
  const { id: schedule_id, cutilei_schedule } = schedule
  const token = AuthService.getToken ()
  const businessId = AuthService.getBusinessId ()
  const canEditValue = AuthService.hasPermission ('bills.items_value', 'edit')
  const canEditComission = AuthService.hasPermission ('bills.items_comission', 'edit')
  const freeService = freeItems.find (s => s.id === schedule.service.id || schedule.bundle_id)
  const canBeFree = !!freeService
  const isFree = freeService?.isFree

  const [editting, setEditting] = useState (false)
  const [showDeleteDialog, setShowDeleteDialog] = useState (false)
  const [deleting, setDeleting] = useState (false)

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  const deleteSchedule = async () => {
    setDeleting (true)
    try {
    await cutileiApi.delete (`/schedules/${schedule.id}`, requestConfig)
      setDeleting (false)
      handleDelete (schedule)
    } catch (error) {
      setDeleting (false)
      logApiError (error)
    }
  }

  const removeScheduleFromBill = async () => {
    setDeleting (true)
    try {
      await cutileiApi.put (
        `/schedules/${schedule.id}/activation`,
        { billActive: false },
        requestConfig
      )
      setDeleting (false)
      handleRemoveFromBill (schedule)
    } catch (error) {
      setDeleting (false)
      logApiError (error)
    }
  }

  const handleServiceSelected = async (name, option, setFieldValue) => {
    const service = option.value

    setFieldValue ('price', service.price)
    setFieldValue ('comission_percentage', service.comission_percentage * 100)
    setFieldValue (name, option)
  }

  const toggleEditting = () => {
    setEditting (!editting)
    handleToggleEdit (!editting)
  }

  const toggleDeleteDialog = () => setShowDeleteDialog (!showDeleteDialog)

  return editting ? (
    <Formik
      initialValues={{
        'professional': {
          label: schedule.professional.nickname,
          value: schedule.professional.id
        },
        'service': {label: schedule.service.name, value: schedule.service},
        'date': schedule.date,
        'time': schedule.time,
        'price': schedule.price,
        'comission_percentage': schedule.comission_percentage * 100
      }}
      onSubmit={async (values, { setSubmitting }) => {
        const scheduleValues = {
          ...values,
          businessId: businessId,
          serviceId: values.service.value.id,
          professionalId: values.professional.value,
          comission_percentage: values.comission_percentage / 100
        }

        try {
          if (!cutilei_schedule) {
            await cutileiApi.post ('/schedules/verify_availability', scheduleValues, requestConfig)
          }

          const { data: schedule } = await cutileiApi.put (
            `/schedules/${schedule_id}`, scheduleValues, requestConfig
          )

          setSubmitting (false)
          setEditting (false)
          handleEdit (schedule)
        } catch (error) {
          logApiError (error)
          setSubmitting (false)
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        isSubmitting,
        setFieldValue,
        setFieldTouched,
        handleBlur, 
        handleSubmit
      }) => (
        <Container editting={editting} enableEditting={enableEditting}>
          <FormField>
            <Select
              name='service'
              placeholder='Serviço...'
              noOptionsMessage='Este profissional ainda não possui serviços cadastrados'
              value={values.service}
              options={services.map (service => ({
                label: service.name,
                value: service
              }))}
              onChange={(name, option) => handleServiceSelected (name, option, setFieldValue)}
              onBlur={setFieldTouched}
              error={errors.service}
              touched={touched.service}
            />
          </FormField>
          <FormField>
            <Select
              name='professional'
              placeholder='Profissional...'
              value={values.professional}
              options={professionals.map (professional => ({
                label: professional.nickname,
                value: professional.id
              }))}
              onChange={setFieldValue}
              onBlur={setFieldTouched}
              error={errors.professional}
              touched={touched.professional}
              containerStyles={{width: '100%'}}
            />
          </FormField>
          <InfoText>
            {DateTime.fromISO (schedule.date).toFormat ('dd/MM/yyyy')}
          </InfoText>
          <InfoText>
            {schedule.time}
          </InfoText>
          <FormField>
            <NumberInput
              id='comission_percentage'
              name='comission_percentage'
              value={values.comission_percentage}
              minValue={0}
              maxValue={100}
              disabled={!canEditComission}
              onChange={setFieldValue}
              style={{width: '100%'}}
            />
          </FormField>
          <Row>
            <FormField>
              <MoneyInput
                name='price'
                placeholder='Preço'
                value={values.price}
                disabled={!canEditValue}
                onValueChange={setFieldValue}
                onBlur={handleBlur ('price')}
                style={{width: '100%'}}
              />
            </FormField>
          </Row>
          <Row>
            <Button type='button' onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? (
                <ButtonLoading style={{margin: 0}}/>
              ) : (
                <FA.FaSave color='#35A7FF' size={16}/>
              )}
            </Button>
            <Button type='button' onClick={toggleEditting}>
              <FA.FaTimes color='#FF3939' size={18}/>
            </Button>
          </Row>
          {/* {errorMessage && (
            <ErrorContainer>
              <InfoText>{errorMessage}</InfoText>
            </ErrorContainer>
          )} */}
        </Container>
      )}
    </Formik>
  ) : (
    <Container
      cutileiSchedule={cutilei_schedule}
      editting={editting}
      enableEditting={enableEditting}
    >
      <Row>
        {cutilei_schedule && <Image src={Logo} alt='Agendamento Cutilei'/>}
        <InfoText>
          {schedule.service.name}
        </InfoText>
      </Row>
      <InfoText>
        {schedule.professional.nickname}
      </InfoText>
      <InfoText>
        {DateTime.fromISO (schedule.date).toFormat ('dd/MM/yyyy')}
      </InfoText>
      <InfoText>
        {schedule.time}
      </InfoText>
      <InfoText>
        {schedule.comission_percentage * 100}%
      </InfoText>
      <Row data-for={isFree ? schedule.id : null} data-tip>
        <MoneyTextMask
          value={(
            isFree ? showFreeItemsValue ? schedule.discount_price : 0 : schedule.price
          ) ?? 0}
          style={{justifySelf: 'center'}}
        />
        {isFree && (
          <ReactTooltip
            id={schedule.id}
            effect='float'
            place='top'
            backgroundColor='#252525'
            tooltipRadius='10'
          >
            {freeService.earned_from === 'bundle' || (
              !schedule.bundle_id && !schedule.loyalty_card_id
            ) && (
              <TooltipLabel>
                O cliente possui este serviço em pacotes adquiridos, portanto ele não será cobrado.
              </TooltipLabel>
            )}
            {freeService.earned_from === 'loyalty_card' && (
              <TooltipLabel>
                O cliente ganhou este serviço em um cartão fidelidade, portanto ele não será cobrado.
              </TooltipLabel>
            )}
            {schedule.discount_price && (
              <TooltipLabel>
                (Valor do serviço no pacote: <MoneyTextMask value={schedule.discount_price} style={{fontSize: 14}}/>)
              </TooltipLabel>
            )}
          </ReactTooltip>
        )}
        {canBeFree && (
          <CheckRow>
            <CheckBox
              id={`is_free_${schedule.id}`}
              value={isFree}
              onValueChange={value => handleToggleFree (schedule, value)}
            />
            <Label htmlFor={`is_free_${schedule.id}`}>
              {freeService.earned_from === 'loyalty_card' ? 'Fidelidade' : 'Pacote'}
            </Label>
          </CheckRow>
        )}
      </Row>
      {!cutilei_schedule && enableEditting && (
        <Row>
          {hasEditPermission && (
            <Button onClick={toggleEditting}>
              <FA.FaEdit color='#35A7FF' size={18}/>
            </Button>
          )}
          {hasDeletePermission && (
            <Button onClick={toggleDeleteDialog}>
              {deleting ? (
                <ButtonLoading style={{margin: 0}}/>
              ) : (
                <FA.FaTrash color='#FF3939' size={16}/>
              )}
            </Button>
          )}
        </Row>
      )}
      <OptionDialog
        visible={showDeleteDialog}
        title='Atenção!'
        message={
          <InfoText>Deseja cancelar este agendamento ou apenas remover o agendamento da comanda?</InfoText>
        }
        options={[
          {label: 'Cancelar agendamento', onClick: deleteSchedule, dangerous: true},
          {label: 'Remover da comanda', onClick: removeScheduleFromBill, dangerous: false}
        ]}
        onClose={toggleDeleteDialog}
        containerStyle={{width: 430}}
        buttonStyle={{width: 180}}
      />
    </Container>
  )
}
